import { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Tooltip from '@givebutter/primitives/tooltip';
import { Campaign, CampaignGivingSpace } from '@givebutter/types';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import {
  faArrowRight,
  faCog,
  faDisplay,
  faFlagCheckered,
  faMessage,
  faQrcode,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Player } from '@lottiefiles/react-lottie-player';
import poweredByGivebutter from 'assets/img/powered-by-givebutter.svg';
import * as confettiAnimation from 'assets/js/lotties/confetti-animation.json';
import * as goalMetConfettiAnimation from 'assets/js/lotties/goal-met-confetti.json';
import { ConnectionStatus, ConnectionStatusProps } from 'components/connection-status';
import { ErrorBoundary } from 'components/error-boundary';
import { ErrorScreen404 } from 'components/error-screen-404/ErrorScreen404';
import { ErrorScreenInvalidCampaign } from 'components/error-screen-invalid-campaign/ErrorScreenInvalidCampaign';
import { GradientCanvas } from 'components/gradient-canvas/GradientCanvas';
import { LoadingScreen } from 'components/loading-screen/LoadingScreen';
import { SettingsEditor } from 'components/settings-editor/SettingsEditor';
import { Shoutout } from 'components/shoutout';
import { motion } from 'framer-motion';
import { useCampaignIdCache } from 'hooks/useCampaignIdCache';
import { useCurrentCampaign } from 'hooks/useCurrentCampaign';
import { LiveDisplaySettings, useStore } from 'hooks/useStore';
import { useSynchronousQueue } from 'hooks/useSynchronousQueue';
import { useWindowSize } from 'hooks/useWindowSize';
import { AnimationItem } from 'lottie-web';
import Pusher from 'pusher-js';
import { useLiveDisplayCampaign } from 'queries/livedisplay';
import { ThemeName, themes } from 'types/theme';

const pusher = new Pusher(import.meta.env.VITE_LIVEDISPLAY_PUSHER_APP_KEY, {
  cluster: import.meta.env.VITE_LIVEDISPLAY_PUSHER_APP_CLUSTER,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const BootstrappedApp = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Tooltip.TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <App />
          <ReactQueryDevtools position={'top-left'} />
        </Tooltip.TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const DONATION_DELAY = 5000;
const MIN_LOAD_TIME = import.meta.env.DEV ? 0 : 3250;
const MIN_SCREEN_WIDTH = 900;
const MIN_SCREEN_HEIGHT = 600;

function App() {
  const [lottieNewDonation, setLottieNewDonation] = useState<AnimationItem | null>(null);
  const [lottieGoalMet, setLottieGoalMet] = useState<AnimationItem | null>(null);
  const [pusherState, setPusherState] = useState<ConnectionStatusProps>({
    lastUpdated: getDateLocaleString(),
    states: { current: 'initialized', previous: 'initialized' },
  });
  const [windowWidth, windowHeight] = useWindowSize();
  const [hasLoadTimePassed, setHasLoadTimePassed] = useState(false);
  const [isMouseMoving, setIsMouseMoving] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [contentDivScale, setContentDivScale] = useState(1);
  const [contentDivTranslate, setContentDivTranslate] = useState(0);
  const [qrCodeContainerRef, setQrCodeContainerRef] = useState<HTMLDivElement | null>(null);
  const [bottomScreenHiddenMarkerRef, setBottomScreenHiddenMarkerRef] =
    useState<HTMLDivElement | null>(null);
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);
  const [oldWindowHeight, setOldWindowHeight] = useState(window.innerHeight);
  const { enqueue } = useSynchronousQueue(DONATION_DELAY);
  const { campaignIdCache, setCampaignIdCache } = useCampaignIdCache(state => {
    return {
      campaignIdCache: state.campaignIdCache,
      setCampaignIdCache: state.setCampaignIdCache,
    };
  });
  const { campaignSlug, campaignId, campaignIdRef } = useCurrentCampaign();
  const {
    doesIdSettingsExist,
    hasDismissedSmallScreenWarning,
    liveDisplaySettings,
    setLiveDisplaySettings,
    setHasDismissedSmallScreenWarning,
  } = useStore(state => {
    return {
      doesIdSettingsExist: !!state.campaigns[campaignId],
      hasDismissedSmallScreenWarning: state.hasDismissedSmallScreenWarning,
      liveDisplaySettings: state.campaigns[campaignId]
        ? state.campaigns[campaignId].liveDisplaySettings
        : state.campaigns.default.liveDisplaySettings,
      setHasDismissedSmallScreenWarning: state.setHasDismissedSmallScreenWarning,
      setLiveDisplaySettings: state.setLiveDisplaySettings,
    };
  });

  useEffect(() => {
    if (!doesIdSettingsExist) {
      setLiveDisplaySettings({
        campaignId: campaignId,
        showAmountRaised: true,
        showQRCode: true,
        showSMSKeyword: true,
        showShoutouts: true,
        theme: themes[ThemeName.aurora],
      });
    }
  }, [campaignId, liveDisplaySettings, setLiveDisplaySettings, doesIdSettingsExist]);

  const [oldLiveDisplaySettings, setOldLiveDisplaySettings] = useState(liveDisplaySettings);

  const { data, isLoading, isError } = useLiveDisplayCampaign({
    campaignSlug,
  });

  useEffect(() => {
    if (data?.id && campaignId != data?.id.toString()) {
      campaignIdRef.current = data?.id.toString();
      if (
        campaignIdCache[campaignSlug] === undefined ||
        campaignIdCache[campaignSlug].campaignId != campaignIdRef.current
      ) {
        setCampaignIdCache({ campaignId: campaignIdRef.current, campaignSlug });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, campaignId, setCampaignIdCache, campaignSlug, campaignIdCache]);

  const [setTheme] = [
    (themeName: ThemeName) => {
      setLiveDisplaySettings({
        ...liveDisplaySettings,
        theme: themes[themeName],
      });
    },
  ];

  const timeout = useRef(0);
  const onMouseMove = () => {
    setIsMouseMoving(true);
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => setIsMouseMoving(false), 3250);
  };

  useEffect(() => {
    setTimeout(() => {
      setHasLoadTimePassed(true);
    }, MIN_LOAD_TIME);
  }, []);

  useEffect(() => {
    function handlePusherStateChange(states: ConnectionStatusProps['states']) {
      setPusherState({ lastUpdated: getDateLocaleString(), states });
    }

    pusher.connection.bind('state_change', handlePusherStateChange);

    return () => {
      pusher.connection.unbind('state_change', handlePusherStateChange);
    };
  }, []);

  useEffect(() => {
    if (!data?.id) {
      return;
    }

    function handleGivingSpaceCreated(newShoutout: CampaignGivingSpace) {
      if (parseInt(newShoutout.donation_amount) === 0) {
        return;
      }
      enqueue([
        () => {
          queryClient.setQueryData<Campaign>(
            ['campaigns', campaignSlug, 'livedisplay'],
            oldData => {
              return {
                ...oldData,
                donors: newShoutout?.campaign?.supporters,
                raised: newShoutout?.campaign?.raised,
                raised_percentage: newShoutout?.campaign?.raised_percentage,
                spaces: [newShoutout, ...(oldData?.spaces ?? [])],
              } as Campaign;
            },
          );

          queryClient.invalidateQueries(['campaigns', campaignSlug, 'livedisplay'], {
            refetchType: 'none',
          });
        },
      ]);
    }

    const channel = pusher.subscribe(`campaigns.${data.id}`);
    channel.bind('giving-space.created', handleGivingSpaceCreated);

    return () => {
      channel.unsubscribe();
      channel.unbind('giving-space.created', handleGivingSpaceCreated);
    };
  }, [campaignSlug, data?.id, enqueue]);

  const hasCampaignGoal = data?.goal ? data.goal > 0 : false;
  const isShowingAmountRaised = liveDisplaySettings.showAmountRaised;

  const hasTextToDonate = data !== undefined && data.text_to_donate !== null;
  const isTextToDonateEnabled = liveDisplaySettings.showSMSKeyword && hasTextToDonate;

  const showConnectionStatus = pusherState.states.current != 'connected';

  useEffect(() => {
    const detectOverlap = () => {
      if (qrCodeContainerRef && bottomScreenHiddenMarkerRef && contentRef) {
        const rect1 = qrCodeContainerRef.getBoundingClientRect();
        const rect2 = bottomScreenHiddenMarkerRef.getBoundingClientRect();
        const overlap = !(rect1.bottom < rect2.top);
        const SCALE_RATE = 0.001;
        const TRANSLATE_RATE = 0.0005;

        if (overlap) {
          setContentDivScale(scale => scale - SCALE_RATE);
          setContentDivTranslate(translate => translate + TRANSLATE_RATE);
        } else if (
          (liveDisplaySettings !== oldLiveDisplaySettings &&
            liveDisplaySettings.theme === oldLiveDisplaySettings.theme) ||
          // detect a big difference in heights - when the window is adjusted quickly
          window.innerHeight - oldWindowHeight > 15
        ) {
          setContentDivScale(1);
          setContentDivTranslate(0);
        } else if (
          // slowly start to scale the content back up to normal
          window.innerHeight > oldWindowHeight &&
          contentDivScale < 1
        ) {
          setContentDivScale(scale => scale + SCALE_RATE);
          setContentDivTranslate(translate => translate - TRANSLATE_RATE);
        }
      }

      setOldWindowHeight(window.innerHeight);
      setOldLiveDisplaySettings(liveDisplaySettings);
    };

    detectOverlap();

    window.addEventListener('resize', detectOverlap);

    return () => {
      window.removeEventListener('resize', detectOverlap);
    };
  }, [
    bottomScreenHiddenMarkerRef,
    contentDivScale,
    contentRef,
    liveDisplaySettings,
    oldLiveDisplaySettings,
    oldWindowHeight,
    qrCodeContainerRef,
  ]);

  useEffect(() => {
    const goalMet = data?.raised_percentage ? data.raised_percentage >= 100 : false;

    if (goalMet && lottieGoalMet !== null) {
      lottieGoalMet.play();
    }
  }, [data?.raised_percentage, lottieGoalMet]);

  if (isError) return <ErrorScreen404 />;
  if (data?.can_use_live_display === false) return <ErrorScreenInvalidCampaign />;
  if (isLoading || !hasLoadTimePassed || campaignId === 'default') return <LoadingScreen />;

  return (
    <>
      <Player
        lottieRef={instance => {
          setLottieGoalMet(instance);
        }}
        loop={false}
        autoplay={false}
        src={JSON.stringify(goalMetConfettiAnimation)}
        className={`pointer-events-none absolute left-0 top-0 z-50 w-full`}
      />
      <Player
        lottieRef={instance => {
          setLottieNewDonation(instance);
        }}
        renderer={'svg'}
        loop={false}
        autoplay={false}
        src={JSON.stringify(confettiAnimation)}
        className={`pointer-events-none absolute right-0 top-0 z-40 w-1/4`}
      />
      <GradientCanvas />
      <div
        className={`relative z-10 grid h-full w-full grid-cols-4 bg-cover bg-center text-white`}
        onTouchStart={() => {
          onMouseMove();
        }}
        onPointerMove={() => {
          onMouseMove();
        }}
      >
        {(windowWidth < MIN_SCREEN_WIDTH || windowHeight < MIN_SCREEN_HEIGHT) &&
          !hasDismissedSmallScreenWarning && (
            <div
              className={`absolute inset-0 z-50 flex items-center justify-center bg-black/80 text-center backdrop-blur-lg`}
            >
              <div className={`flex max-w-sm flex-col items-center justify-center gap-y-2 p-4`}>
                <div className="relative text-red-500">
                  <FontAwesomeIcon icon={faDisplay} size={'3x'} />
                  <FontAwesomeIcon
                    icon={faUpRightAndDownLeftFromCenter}
                    size={'lg'}
                    className={`absolute left-[17px] top-[10px] text-red-500`}
                  />
                </div>
                <div className={`text-2xl font-semibold`}>Your browser is too small</div>
                <div>
                  Increase the dimensions of your browser to a minimum of {MIN_SCREEN_WIDTH}px by{' '}
                  {MIN_SCREEN_HEIGHT}px. If you&apos;re still having trouble, try zooming out by
                  pressing the &apos;CMD/CTRL + Minus&apos; keys.
                </div>
                <div>
                  <button
                    onClick={() => {
                      setHasDismissedSmallScreenWarning(true);
                    }}
                    className="mt-3 flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-900 hover:bg-gray-100"
                  >
                    Continue anyway
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>
            </div>
          )}

        <div
          className={`flex px-12 pt-12 ${
            liveDisplaySettings.showShoutouts ? 'col-span-3' : 'col-span-4'
          }`}
        >
          <div
            ref={setContentRef}
            className={`flex w-full origin-top flex-col gap-y-11 ${getContentContainerStyles(
              liveDisplaySettings,
              isShowingAmountRaised,
              isTextToDonateEnabled,
            )}`}
            style={{
              transform: `scale(${contentDivScale}) translateY(-${contentDivTranslate}%)`,
            }}
          >
            <div
              className={`flex flex-col ${getCampaignTitleStyles(
                liveDisplaySettings,
                isShowingAmountRaised,
                isTextToDonateEnabled,
              )}`}
            >
              <h1
                className={`font-medium drop-shadow-lg ${getCampaignCreatorNameStyles(
                  liveDisplaySettings,
                  isShowingAmountRaised,
                  isTextToDonateEnabled,
                )}`}
              >
                {data.creator_name}
              </h1>
              <h1
                className={`text-6xl font-semibold drop-shadow-lg ${getCampaignNameStyles(
                  liveDisplaySettings,
                  isShowingAmountRaised,
                  isTextToDonateEnabled,
                )}`}
              >
                {data.title}
              </h1>
            </div>
            {isShowingAmountRaised && (
              <div className={'flex flex-col gap-y-2'}>
                <div className="relative">
                  <span className="mr-4 text-7xl font-bold drop-shadow-lg xl:text-8xl 2xl:text-9xl">
                    ${maskDecimal(data.raised)}
                  </span>
                  <span className="text-xl drop-shadow-lg xl:text-3xl">
                    {maskWholeNumber(data.donors)} supporter
                    {data.donors != 1 && 's'}
                  </span>
                  {hasCampaignGoal && (
                    <span className="absolute bottom-0 right-0 flex items-center text-xl font-bold drop-shadow-lg xl:text-3xl">
                      ${maskDecimal(data?.goal)}
                      <FontAwesomeIcon icon={faFlagCheckered} size={'xs'} className="ml-2" />
                    </span>
                  )}
                </div>

                {hasCampaignGoal && (
                  <div className="rounded-full bg-black/5 p-1.5 shadow-[inset_0px_0px_67px_rgba(0,0,0,0.25)] backdrop-blur-[50px] 2xl:p-2">
                    <motion.div
                      initial={{ width: `3rem` }}
                      animate={{
                        width: `${data.raised_percentage}%`,
                      }}
                      transition={{ duration: 1.5, ease: 'easeIn' }}
                      className={`h-8 min-w-12 max-w-full rounded-full bg-gradient-to-b from-[#14FF91] to-[#12B76A] 2xl:h-12`}
                    >
                      &nbsp;
                    </motion.div>
                  </div>
                )}
              </div>
            )}

            {(liveDisplaySettings.showQRCode || isTextToDonateEnabled) && (
              <div
                ref={setQrCodeContainerRef}
                className={`flex items-center ${
                  liveDisplaySettings.showShoutouts ? 'justify-between' : 'justify-center'
                }`}
              >
                {liveDisplaySettings.showQRCode && (
                  <div
                    className={`flex flex-col gap-y-2 ${getQrCodeStyles(
                      liveDisplaySettings,
                      isTextToDonateEnabled,
                    )}`}
                  >
                    <div className="flex items-center text-lg drop-shadow-lg xl:text-3xl">
                      <FontAwesomeIcon icon={faQrcode} />
                      &nbsp;Scan-to-Donate
                    </div>
                    <div className="aspect-square rounded-2xl">
                      <div className="flex h-full w-full items-center justify-center rounded-2xl bg-black p-4">
                        <div className={`w-full rounded-2xl bg-white p-4 text-center xl:p-8`}>
                          <img alt="QR Code" src={data.qr_code_url} className="h-full w-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {liveDisplaySettings.showQRCode && isTextToDonateEnabled && (
                  <div className="w-[20%]">
                    <div className="text-center text-2xl drop-shadow-lg xl:text-6xl">OR</div>
                  </div>
                )}

                {isTextToDonateEnabled && (
                  <div
                    className={`flex flex-col gap-y-2 ${getTextToDonateStyles(
                      liveDisplaySettings,
                    )}`}
                  >
                    <div className="flex items-center text-lg drop-shadow-lg xl:text-3xl">
                      <FontAwesomeIcon icon={faMessage} />
                      &nbsp;Text-to-Donate
                    </div>
                    <div className="flex aspect-square flex-col items-center justify-evenly rounded-2xl bg-black px-10 py-4 text-center">
                      <div className="3xl:text-5xl rounded-full bg-white p-4 text-2xl font-bold text-black xl:text-3xl 2xl:text-4xl">
                        &quot;{data.text_to_donate}&quot;
                      </div>
                      <div className="grid w-full grid-cols-3 items-center gap-x-1">
                        <div className="h-px bg-white"></div>
                        <div className="3xl:text-6xl text-center text-2xl xl:text-4xl 2xl:text-5xl">
                          to
                        </div>
                        <div className="h-px bg-white"></div>
                      </div>
                      <div className="3xl:text-8xl text-lg font-black !leading-none lg:text-5xl xl:text-7xl">
                        53-555
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          ref={setBottomScreenHiddenMarkerRef}
          className={`absolute bottom-0 left-0 right-0 h-12`}
        />

        {liveDisplaySettings.showShoutouts && (
          <div className="relative col-span-1 overflow-hidden bg-black/25 shadow-2xl backdrop-blur-2xl">
            <Shoutout slug={campaignSlug} lottieNewDonation={lottieNewDonation} />
          </div>
        )}

        <button
          className={`${
            !isMenuVisible && isMouseMoving ? 'opacity-100' : 'pointer-events-none opacity-0'
          } ${
            showConnectionStatus ? 'bottom-12' : 'bottom-4'
          } pointer absolute left-4 z-50 rounded-2xl bg-white p-4 font-semibold text-black shadow-lg transition-all duration-300`}
          onClick={() => {
            setIsMenuVisible(true);
          }}
        >
          <FontAwesomeIcon icon={faCog} /> View Settings
        </button>

        {showConnectionStatus && (
          <div className={`absolute bottom-0 left-0`}>
            <ConnectionStatus states={pusherState.states} lastUpdated={pusherState.lastUpdated} />
          </div>
        )}

        <div
          className={`bottom-0 ${
            liveDisplaySettings.showShoutouts ? 'p-10 backdrop-blur-3xl' : 'pb-4 pr-8'
          } absolute right-0 z-20 w-[25%] text-center`}
        >
          <img
            className={`inline-block w-full max-w-[16rem] ${
              liveDisplaySettings.showShoutouts ? '' : 'float-right'
            }`}
            src={poweredByGivebutter}
            alt="Powered by Givebutter"
          />
        </div>

        <SettingsEditor
          hasTextToDonate={hasTextToDonate}
          isMenuVisible={isMenuVisible}
          setIsMenuVisible={setIsMenuVisible}
          isMouseMoving={isMouseMoving}
          themes={themes}
          setTheme={setTheme}
          liveDisplaySettings={liveDisplaySettings}
          liveDisplayUrl={data.live_display_url}
          campaignUrl={data.url}
          setLiveDisplaySettings={setLiveDisplaySettings}
        />
      </div>
    </>
  );
}

function getContentContainerStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !liveDisplaySettings.showQRCode &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' items-center justify-center';
  }

  return styles;
}

function getQrCodeStyles(liveDisplaySettings: LiveDisplaySettings, isTextToDonateEnabled: boolean) {
  let styles = '';

  if (!liveDisplaySettings.showShoutouts) {
    styles += ' w-[25%]';
  } else if (isTextToDonateEnabled) {
    styles += ' w-[33%]';
  } else {
    styles += ' w-[35%]';
  }

  return styles;
}
function getTextToDonateStyles(liveDisplaySettings: LiveDisplaySettings) {
  let styles = '';

  if (!liveDisplaySettings.showShoutouts) {
    styles += ' w-[25%]';
  } else if (liveDisplaySettings.showQRCode) {
    styles += ' w-[33%]';
  } else {
    styles += ' w-[35%]';
  }

  return styles;
}

function getCampaignCreatorNameStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !liveDisplaySettings.showQRCode &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' text-6xl';
  } else {
    styles += ' text-5xl';
  }

  return styles;
}

function getCampaignNameStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !liveDisplaySettings.showQRCode &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += 'lg:text-9xl';
  } else {
    styles += 'lg:text-8xl';
  }

  return styles;
}

function getCampaignTitleStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !liveDisplaySettings.showQRCode &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' gap-y-8 text-center';
  } else {
    styles += 'gap-y-2 ';
  }

  return styles;
}

function getDateLocaleString() {
  return new Date(Date.now()).toLocaleString();
}

export { themes };

export default BootstrappedApp;
